<template>
  <div class="full-height">
    <div>
      <v-progress-circular
        v-if="loading.dashboard"
        indeterminate
        color="primary"
        class="my-8"
        :size="50"
        :width="4"
      ></v-progress-circular>
      <p
        v-else-if="
          !globalRows.length &&
          !loading.dashboard &&
          !filteredProgramGroups.length &&
          !summaryPrograms.length &&
          !loading.summary
        "
      >
        {{ $t("programDashboard.empty") }}
      </p>
      <div class="global-rows-section" v-if="globalRows.length > 0">
        <DashboardRowRenderer
          v-for="row in globalRows"
          :key="rowKey(row)"
          :row="row"
          :metrics="metrics"
          :metrics-loading="loading.metrics"
        />
      </div>
      <div class="your-programs-section" v-if="showYourPrograms">
        <div class="your-programs-toolbar">
          <!-- Left: icon + title -->
          <div class="toolbar-header">
            <div class="toolbar-icon-box">
              <v-icon color="white" size="20">mdi-star-check</v-icon>
            </div>
            <span class="toolbar-title">{{ $t("programDashboard.yourPrograms.title") }}</span>
          </div>

          <!-- Divider -->
          <div class="toolbar-divider"></div>

          <ProgramFilterSortMenus
            :filter-options="filterOptions"
            :selected-filters="selectedFilters"
            :sort-options="sortOptions"
            :sort-by="sortBy"
            :sort-order="sortOrder"
            @toggle-filter="toggleFilter"
            @update-sort="onUpdateSort"
          />

          <!-- Spacer -->
          <div class="toolbar-spacer"></div>

          <!-- Search -->
          <v-text-field
            v-model="searchQuery"
            :placeholder="$t('programDashboard.yourPrograms.searchPlaceholder')"
            prepend-inner-icon="mdi-magnify"
            hide-details
            dense
            outlined
            class="toolbar-search"
          />
        </div>
        <v-progress-linear v-if="loading.summary" indeterminate />
        <template v-else-if="visibleProgramGroups.length > 0">
          <div
            class="program-group"
            v-for="group in visibleProgramGroups"
            :key="group.program.programId"
          >
            <ProgramSummaryCard
              :program="group.program"
              v-bind="earningsProps(group.program)"
              :button-color="group.buttonColor"
              :button-text-color="group.buttonTextColor"
              @open-program="loadProgram"
            />
            <DashboardRowRenderer
              v-for="subRow in group.subRows"
              :key="rowKey(subRow)"
              :row="subRow"
              :metrics="metrics"
              :metrics-loading="loading.metrics"
            />
          </div>
        </template>
        <Typography v-else>{{ $t("programDashboard.yourPrograms.noResults") }}</Typography>
        <v-btn v-if="hasMorePrograms" class="load-more-btn" @click="loadMore" block text>
          {{ $t("programDashboard.yourPrograms.loadMore") }}
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
// Services
import ProgramService from "@/services/ProgramService.js";

// Components
import ProgramSummaryCard from "@/components/participant/programs/ProgramSummaryCard.vue";
import ProgramFilterSortMenus from "@/components/participant/programs/ProgramFilterSortMenus.vue";
import DashboardRowRenderer from "@/components/program/participant/dashboard/DashboardRowRenderer.vue";
import { isRenderableDashboardColumn } from "../components/program/participant/dashboard/DashboardRowRenderer.vue";
import Typography from "@/components/generic/Typography.vue";

// Vuex
import { mapState } from "vuex";

// Utils
import fz from "fuzzaldrin-plus";
import { isDynamicIncentive } from "@/utils/isDynamicIncentive";
import {
  numericPosition,
  compareNullableNumbers,
  compareSubRows,
  rowKey as rowKeyUtil,
  compareRowIds as compareRowIdsUtil,
} from "@/utils/dashboardOrder";

export default {
  name: "ProgramDashboardV2",
  title() {
    return this.$t("programDashboard.title");
  },
  components: {
    ProgramSummaryCard,
    ProgramFilterSortMenus,
    DashboardRowRenderer,
    Typography,
  },
  data() {
    return {
      loading: { dashboard: false, metrics: false, summary: false },
      summaryPrograms: [],
      metrics: null,
      metricsConfig: null,
      loadingError: false,

      // Dashboard
      dashboardRows: [],

      // Virtual pagination
      visibleCount: 25,

      // Sort / Filter / Search
      sortBy: "position",
      sortOrder: "asc",
      selectedFilters: ["active"],
      searchQuery: "",
    };
  },
  created() {
    if (
      !(
        this.userProfile &&
        this.userProfile.Client &&
        this.userProfile.Client.displayProgramDashboardPage
      )
    ) {
      if (this.userProfile && this.userProfile.Client && this.userProfile.Client.displayProgramPage)
        this.$router.push({ name: "programs" });
      else this.$router.push("/");
      return;
    }

    if (this.$auth) {
      Promise.all([
        this.getProgramDashboard(),
        this.getUserSummary(),
        this.getMetricsSummary(),
      ]).then(() => {
        this.$nextTick(() => this.restoreScrollPosition());
      });
    }
  },
  methods: {
    rowKey(row) {
      return rowKeyUtil(row);
    },
    compareRowIds(a, b) {
      return compareRowIdsUtil(a, b);
    },
    compareRows(a, b) {
      return compareNullableNumbers(a.position, b.position) || this.compareRowIds(a, b);
    },
    hasRenderableColumns(row) {
      return (row?.DashboardColumns ?? []).some(isRenderableDashboardColumn);
    },
    restoreScrollPosition() {
      const saved = sessionStorage.getItem("dynamicProgramReturnScrollY");
      if (saved == null) return;
      sessionStorage.removeItem("dynamicProgramReturnScrollY");
      window.scrollTo(0, Number(saved));
    },
    handleAnimation: function (anim) {
      this.anim = anim;
    },
    async getProgramDashboard() {
      this.loading.dashboard = true;
      try {
        var dashResult = await ProgramService.getProgramDashboardV2();
        var apiRows = (dashResult && dashResult.result && dashResult.result.rows) || [];
        var rows = [...apiRows].sort(this.compareRows);
        this.dashboardRows = rows;
        this.extractMetricsConfig(rows);
      } catch (e) {
        // error handled silently; loading flag cleared in finally
      } finally {
        this.loading.dashboard = false;
      }
    },
    extractMetricsConfig(rows) {
      for (const row of rows) {
        if (row.DashboardRowType && row.DashboardRowType.type === "metrics-summary") {
          const col = row.DashboardColumns && row.DashboardColumns[0];
          if (col && col.config && col.config.metrics) {
            this.metricsConfig = col.config.metrics;
            return;
          }
        }
      }
    },
    async getUserSummary() {
      this.loading.summary = true;
      try {
        const result = await ProgramService.getUserSummary();
        const rawPrograms = ((result && result.rows) || result || []).map((program) => ({
          ...program,
          progressPercent:
            program.progress && program.progress.progressPercent != null
              ? program.progress.progressPercent
              : program.progressPercent || 0,
          progressValue:
            program.progress && program.progress.progressValue != null
              ? program.progress.progressValue
              : program.progressValue || null,
        }));
        // Filter out PROCORE only; CLAIM programs shown per #182702
        this.summaryPrograms = rawPrograms.filter((p) => {
          var type = (p.type || "").toUpperCase();
          return type !== "PROCORE";
        });
      } catch (e) {
        // error handled silently; loading flag cleared in finally
      } finally {
        this.loading.summary = false;
      }
    },
    earningsProps(program) {
      const { earnings } = program;
      if (!earnings) return {};
      if (isDynamicIncentive(program)) {
        return {
          earnings: earnings.accumulatedReward,
          basis: earnings.basis,
          isDollarBudget: earnings.awardId === 1,
        };
      }
      return { earnings: Number(earnings.accumulatedReward) || 0 };
    },
    async getMetricsSummary() {
      this.loading.metrics = true;
      try {
        const result = await ProgramService.getMetricsSummary();
        this.metrics = result || null;
      } catch (e) {
        // error handled silently; loading flag cleared in finally
      } finally {
        this.loading.metrics = false;
      }
    },
    onUpdateSort({ sortBy, sortOrder }) {
      this.sortBy = sortBy;
      this.sortOrder = sortOrder;
    },
    toggleFilter(value) {
      this.selectedFilters = this.selectedFilters.includes(value)
        ? this.selectedFilters.filter((filter) => filter !== value)
        : [...this.selectedFilters, value];
    },
    programFilterBucket(program) {
      const state = program.programState;
      if (state === "inactive") return "archived";
      if (state === "coming_soon") return "future";
      return "active";
    },
    loadMore() {
      this.visibleCount += 25;
    },
    loadProgram(program) {
      if (isDynamicIncentive(program)) {
        sessionStorage.setItem("dynamicProgramReturnScrollY", String(window.scrollY));
        this.$router.push({
          name: "dynamicprogramdetail",
          params: { programId: program.programId.toString() },
        });
        return;
      }
      this.$router.push({
        name: "programdetails",
        query: { returnTo: "program-dashboard" },
        params: {
          programPreloaded: program,
          programId: program.programId.toString(),
          sourcePage: "programs",
        },
      });
    },
  },
  computed: {
    ...mapState(["userProfile", "postItArray"]),
    filterOptions() {
      return ["active", "future", "archived", "all"].map((value) => ({
        value,
        label: this.$t(`programDashboard.filters.${value}`),
      }));
    },
    sortOptions() {
      return ["position", "name", "progress", "timeRemaining"].map((value) => ({
        value,
        label: this.$t(`programDashboard.sort.${value}`),
      }));
    },
    isMobile() {
      return this.$vuetify.breakpoint.xs || this.$vuetify.breakpoint.sm;
    },
    showYourPrograms() {
      return !this.loading.dashboard && (this.loading.summary || this.summaryPrograms.length > 0);
    },
    globalRows() {
      return this.dashboardRows.filter(
        (row) => row.programId === null && this.hasRenderableColumns(row)
      );
    },
    programGroups() {
      return this.summaryPrograms.map((program, summaryIndex) => {
        const programRows = this.dashboardRows.filter((r) => r.programId === program.programId);
        const defaultRow = programRows.find(
          (r) =>
            r.programId === program.programId && r.DashboardRowType?.type === "default_program_row"
        );
        const subRows = programRows
          .filter(
            (r) =>
              r.DashboardRowType?.type !== "default_program_row" && this.hasRenderableColumns(r)
          )
          .sort(compareSubRows);
        const fallbackPositions = subRows
          .map((row) => numericPosition(row.position))
          .filter((position) => position !== null);
        const defaultPosition = numericPosition(defaultRow?.position);
        const config = defaultRow?.DashboardColumns?.[0]?.config || {};
        return {
          program,
          position:
            defaultPosition !== null
              ? defaultPosition
              : fallbackPositions.length
              ? Math.min(...fallbackPositions)
              : null,
          summaryIndex,
          buttonColor: config.buttonBackgroundColor || null,
          buttonTextColor: config.buttonTextColor || null,
          subRows,
        };
      });
    },
    filteredProgramGroups() {
      let groups = this.programGroups;

      if (!this.selectedFilters.includes("all")) {
        groups = this.selectedFilters.length
          ? groups.filter((g) => this.selectedFilters.includes(this.programFilterBucket(g.program)))
          : [];
      }

      // Search
      if (this.searchQuery && this.searchQuery.trim().length > 0) {
        const preparedQuery = fz.prepareQuery(this.searchQuery);
        groups = groups.filter(
          (g) => fz.score(g.program.name || "", this.searchQuery, { preparedQuery }) > 0
        );
      }

      // Sort
      const now = new Date();
      const sortBy = this.sortBy;
      const sortOrder = this.sortOrder;
      groups = [...groups].sort((a, b) => {
        if (sortBy === "timeRemaining") {
          const daysA = a.program.endDate
            ? Math.ceil((new Date(a.program.endDate) - now) / 86400000)
            : null;
          const daysB = b.program.endDate
            ? Math.ceil((new Date(b.program.endDate) - now) / 86400000)
            : null;
          if (daysA === null && daysB === null) return 0;
          if (daysA === null) return 1;
          if (daysB === null) return -1;
          const comparison = daysA - daysB;
          return sortOrder === "desc" ? -comparison : comparison;
        }
        let comparison = 0;
        if (sortBy === "position") {
          const positionA = numericPosition(a.position);
          const positionB = numericPosition(b.position);
          if (positionA === null && positionB === null) return a.summaryIndex - b.summaryIndex;
          if (positionA === null) return 1;
          if (positionB === null) return -1;
          comparison = positionA - positionB;
          if (comparison !== 0) return sortOrder === "desc" ? -comparison : comparison;
          return a.summaryIndex - b.summaryIndex;
        } else if (sortBy === "name") {
          comparison = (a.program.name || "").localeCompare(b.program.name || "");
        } else if (sortBy === "progress") {
          comparison = (a.program.progressPercent || 0) - (b.program.progressPercent || 0);
        }
        return sortOrder === "desc" ? -comparison : comparison;
      });

      return groups;
    },
    visibleProgramGroups() {
      return this.filteredProgramGroups.slice(0, this.visibleCount);
    },
    hasMorePrograms() {
      return this.visibleCount < this.filteredProgramGroups.length;
    },
  },
  watch: {
    visibleCount() {
      this._vcChanged = true;
      this.$nextTick(() => {
        this._vcChanged = false;
      });
    },
    filteredProgramGroups() {
      if (!this._vcChanged) this.visibleCount = 25;
    },
    sortBy() {
      if (!this._vcChanged) this.visibleCount = 25;
    },
  },
};
</script>

<style scoped>
/* Break out of App.vue's 10px left/right wrapper padding */
.full-height {
  margin-left: -10px;
  margin-right: -10px;
}

/* ── Your Programs Section ─────────────────────────────────────────────────── */
.your-programs-section {
  background: #ffffff;
  border: 1px solid rgba(36, 55, 62, 0.12);
  box-shadow: 0px 4px 12px 0px rgba(205, 208, 210, 0.24);
  margin-top: 24px;
  overflow: hidden;
}

.your-programs-toolbar {
  display: flex;
  align-items: center;
  padding: 16px 32px;
  gap: 8px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

.toolbar-header {
  display: flex;
  align-items: center;
  gap: 16px;
}

.toolbar-icon-box {
  background: #002a4e;
  border-radius: 4px;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.toolbar-title {
  font-family: "Avenir", sans-serif;
  font-weight: 500;
  font-size: 20px;
  line-height: 28px;
  color: #000000;
  text-align: left;
}

.toolbar-divider {
  width: 1px;
  height: 46px;
  background: #d9d9d9;
  margin: 0 8px;
  flex-shrink: 0;
}

.toolbar-spacer {
  flex: 1;
}

.toolbar-search {
  max-width: 463px;
  width: 463px;
}

.program-group {
  padding: 24px 16px;
  border-top: 1px solid rgba(0, 0, 0, 0.12);
}

@media (max-width: 959px) {
  .program-group {
    padding: 24px 12px;
  }
}

.load-more-btn {
  color: #077daf !important;
  font-family: "Avenir", Helvetica, Arial, sans-serif !important;
  font-weight: 350 !important;
  font-size: 14px !important;
  line-height: 18px !important;
  text-transform: uppercase !important;
  letter-spacing: 0.0893em !important;
  border-radius: 18px !important;
  margin: 16px auto !important;
}

/* small tablet and phone breakpoint, moving progress and title to bottom */
@media only screen and (max-width: 700px) {
  .small-screen-title .program-date-string {
    flex-wrap: wrap;
  }
}
/* small screens, so that the filters are layed out in a row */
@media only screen and (max-width: 960px) and (min-width: 600px) {
  .small-screen-div {
    justify-content: space-between;
    align-items: baseline;
  }
}
</style>

<style>
.pgm-dashboard-container {
  word-break: break-word;
  width: 100%;
  color: black;
}

.headline-container,
.billboard-container {
  flex-direction: column;
}

/* Vue XS */
@media only screen and (max-width: 600px) {
  .headline-container {
    font-size: 0.7em;
  }
  .headline-container .overlay-text {
    position: relative;
    right: auto;
    top: auto;
    width: 100%;
  }
}

.pgm-dashboard-text-align-left {
  left: 3%;
}
.pgm-dashboard-text-align-center {
  left: 50%;
  transform: translateX(-50%);
}
.pgm-dashboard-text-align-right {
  right: 3%;
}

.pgm-dashboard-text-v-align-top {
  top: 3%;
}
.pgm-dashboard-text-v-align-center {
  top: 50%;
  transform: translateY(-50%);
}
.pgm-dashboard-text-v-align-bottom {
  bottom: 3%;
}
</style>
